/**
 * AI Master Data Service
 */
import api from '../shared/services/api';

export interface MasterDataItem {
  id: string;
  sku?: string;
  supplier_name?: string;
  raw_description: string;
  enriched_description?: string;
  hs_code?: string;
  hs_code_confidence?: number;
  hs_code_reasoning?: string;
  is_dual_use?: boolean;
  dual_use_al_numbers?: string;
  eccn_reasoning?: string;
  dual_use_check_failed?: boolean;
  origin_country?: string;
  origin_reasoning?: string;
  status: 'pending' | 'enriched' | 'needs_review' | 'confirmed' | 'duplicate';
  duplicate_of?: string;
  source_module?: string;
  source_reference?: string;
  source_transaction_id?: string;
  created_at: string;
}

export interface ImportResponse {
  imported: number;
  duplicates: number;
  import_batch: string;
}

export interface BulkEnrichResponse {
  enriched: number;
  needs_review: number;
  failed: number;
  skipped?: number;
}

export interface MasterDataStats {
  total: number;
  by_status: Record<string, number>;
  enriched_pct: number;
  needs_review: number;
  dual_use: number;
}

export const importCatalog = async (file: File): Promise<ImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/v1/master-data/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const listMasterDataItems = async (
  statusFilter?: string,
  skip = 0,
  limit = 100,
): Promise<MasterDataItem[]> => {
  const response = await api.get('/api/v1/master-data/items', {
    params: { status_filter: statusFilter, skip, limit },
  });
  return response.data;
};

export const enrichItem = async (id: string): Promise<MasterDataItem> => {
  const response = await api.post(`/api/v1/master-data/items/${id}/enrich`);
  return response.data;
};

export const enrichItemsBulk = async (ids: string[]): Promise<BulkEnrichResponse> => {
  const response = await api.post('/api/v1/master-data/items/enrich-bulk', { ids });
  return response.data;
};

export const updateItem = async (
  id: string,
  data: { hs_code?: string; enriched_description?: string; status?: string },
): Promise<MasterDataItem> => {
  const response = await api.put(`/api/v1/master-data/items/${id}`, data);
  return response.data;
};

export const deleteItem = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/master-data/items/${id}`);
};

export const createItem = async (data: {
  raw_description: string;
  sku?: string;
  supplier_name?: string;
}): Promise<MasterDataItem> => {
  const response = await api.post('/api/v1/master-data/items', data);
  return response.data;
};

export const getStats = async (): Promise<MasterDataStats> => {
  const response = await api.get('/api/v1/master-data/stats');
  return response.data;
};

export const searchItems = async (q: string, limit = 10): Promise<MasterDataItem[]> => {
  const response = await api.get('/api/v1/master-data/search', { params: { q, limit } });
  return response.data;
};

export const bulkSetStatus = async (ids: string[], status: string): Promise<{ updated: number }> => {
  const response = await api.post('/api/v1/master-data/items/bulk-status', { ids, status });
  return response.data;
};

export const bulkDeleteItems = async (ids: string[]): Promise<{ deleted: number }> => {
  const response = await api.post('/api/v1/master-data/items/bulk-delete', { ids });
  return response.data;
};

export const bulkCommand = async (
  ids: string[],
  command: string,
): Promise<{ op: string; affected: number; explanation: string }> => {
  const response = await api.post('/api/v1/master-data/items/bulk-command', { ids, command });
  return response.data;
};

export interface ComplianceAlert {
  id: string;
  product_id: string;
  product_description?: string;
  alert_type: 'hs_drift' | 'newly_dual_use' | 'origin_proof_expiring' | 'origin_proof_missing';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  details?: string;
  status: 'open' | 'acknowledged';
  created_at: string;
}

export const getAlerts = async (statusFilter = 'open'): Promise<ComplianceAlert[]> => {
  const response = await api.get('/api/v1/master-data/alerts', {
    params: { status_filter: statusFilter },
  });
  return response.data;
};

export const acknowledgeAlert = async (id: string): Promise<ComplianceAlert> => {
  const response = await api.post(`/api/v1/master-data/alerts/${id}/acknowledge`);
  return response.data;
};

export const runRadar = async (): Promise<{ new_alerts: number }> => {
  const response = await api.post('/api/v1/master-data/run-radar');
  return response.data;
};

export const resetClassifierLearning = async (): Promise<{
  superseded: number;
  deleted_records: number;
}> => {
  const response = await api.post('/api/v1/master-data/reset-classifier-learning');
  return response.data;
};

export interface DutyEstimate {
  available: boolean;
  hs_code: string;
  message?: string;
  taric_description?: string;
  duty_rate_raw?: string;
  duty_pct?: number | null;
  duty_amount?: number | null;
  customs_value?: number;
  vat_pct?: number;
  vat_amount?: number | null;
  landed_cost?: number | null;
  currency?: string;
  is_estimate?: boolean;
  note?: string;
}

export const estimateDuty = async (
  id: string,
  customsValue: number,
  vatPct?: number,
): Promise<DutyEstimate> => {
  const response = await api.post(`/api/v1/master-data/items/${id}/duty-estimate`, {
    customs_value: customsValue,
    vat_pct: vatPct,
  });
  return response.data;
};

export const exportCatalog = async (format: 'csv' | 'excel'): Promise<void> => {
  const response = await api.get('/api/v1/master-data/export', {
    params: { format },
    responseType: 'blob',
  });
  const ext = format === 'excel' ? 'xlsx' : 'csv';
  const mime =
    format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';
  const date = new Date().toISOString().slice(0, 10);
  const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `master_data_${date}.${ext}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
